import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Tooltip } from 'antd';
import {
  DragOutlined,
  CloseOutlined,
  MinusOutlined,
  PlusOutlined,
  ExpandOutlined,
  CompressOutlined,
} from '@ant-design/icons';
import { formatCurrency, formatExactCurrency } from './utils';

export type RevenueTargetStateBreakdown = {
  state: string;
  target: number;
  achieved: number;
  remaining: number;
  percent: number;
  companyCount?: number;
  salesRepCount?: number;
  topSalesRepName?: string;
  topSalesRepCity?: string;
  topSalesRepCompanyCount?: number;
  topCity?: string;
  topCityCount?: number;
  companies?: Array<{ name: string; userid?: string; city?: string }>;
  locations?: unknown;
};

interface RevenueTargetMetricsCardProps {
  visible: boolean;
  title: string;
  highlightedState?: string;
  totalTarget: number;
  totalAchieved: number;
  totalRemaining: number;
  achievementPercent: number;
  breakdown: RevenueTargetStateBreakdown[];
  onViewBreakdown?: () => void;
  onClose?: () => void;
}

const CardContainer = styled.div<{ $x: number; $y: number; $maximized: boolean }>`
  position: fixed;
  top: ${(props) => props.$y}px;
  left: ${(props) => props.$x}px;
  width: ${(props) => (props.$maximized ? 420 : 360)}px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15);
  border: 1px solid #eef0f3;
  z-index: 1010;
  overflow: hidden;
  transition: width 0.2s ease, height 0.2s ease;
`;

const Header = styled.div<{ $dragging: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f1f4ff 100%);
  cursor: ${(props) => (props.$dragging ? 'grabbing' : 'grab')};
  user-select: none;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #1a237e;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: #e8eaf6;
  color: #3949ab;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: #c5cae9;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Content = styled.div<{ $collapsed: boolean }>`
  display: ${(props) => (props.$collapsed ? 'none' : 'flex')};
  flex-direction: column;
  padding: 16px;
  gap: 16px;
  overflow-y: auto;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const SummaryCard = styled.div<{ $variant?: 'primary' | 'success' | 'warning' }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: 10px;
  background: ${(props) =>
    props.$variant === 'primary'
      ? 'linear-gradient(135deg, rgba(30,136,229,0.1) 0%, rgba(25,118,210,0.15) 100%)'
      : props.$variant === 'success'
      ? 'linear-gradient(135deg, rgba(24,179,96,0.1) 0%, rgba(17,128,87,0.15) 100%)'
      : props.$variant === 'warning'
      ? 'linear-gradient(135deg, rgba(255,167,38,0.12) 0%, rgba(255,143,0,0.16) 100%)'
      : '#f8f9fb'};
  border: 1px solid rgba(148, 163, 184, 0.2);
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SummaryValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
`;

const AchievementPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(24, 179, 96, 0.12);
  color: #15803d;
  font-size: 12px;
  font-weight: 600;
  align-self: flex-start;
`;

const EmptyState = styled.div`
  padding: 36px 20px;
  border-radius: 12px;
  background: #f8fafc;
  text-align: center;
  color: #64748b;
  font-size: 13px;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const BreakdownSummary = styled.div`
  font-size: 12px;
  color: #4b5563;
`;

const ViewMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 999px;
  background: #1a73e8;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: #1765c1;
    box-shadow: 0 6px 18px rgba(26, 115, 232, 0.28);
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    background: #cbd5f0;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const formatPercent = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '0%';
  }
  return `${value.toFixed(1)}%`;
};

export const RevenueTargetMetricsCard: React.FC<RevenueTargetMetricsCardProps> = ({
  visible,
  title,
  highlightedState,
  totalTarget,
  totalAchieved,
  totalRemaining,
  achievementPercent,
  breakdown,
  onViewBreakdown,
  onClose,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 28,
    y: 140,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const highlightedLabel = useMemo(() => {
    if (!highlightedState) {
      return 'All states selected';
    }
    return `${highlightedState} focus`;
  }, [highlightedState]);

  const startDrag = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!cardRef.current) return;
      if (event.button !== 0) return;
      const target = event.target as HTMLElement;
      if (target.closest('button')) return;
      setIsDragging(true);
      const rect = cardRef.current.getBoundingClientRect();
      dragOffset.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    },
    [],
  );

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging) return;
    setPosition(() => {
      const nextX = event.clientX - dragOffset.current.x;
      const nextY = event.clientY - dragOffset.current.y;
      return { x: Math.max(12, nextX), y: Math.max(12, nextY) };
    });
  }, [isDragging]);

  const stopDrag = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopDrag);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopDrag);
    };
  }, [isDragging, handleMouseMove, stopDrag]);

  useEffect(() => {
    if (!visible) return;
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const maxX = window.innerWidth - rect.width - 16;
    const maxY = window.innerHeight - rect.height - 16;
    setPosition((prev) => ({
      x: Math.min(Math.max(prev.x, 12), maxX > 12 ? maxX : prev.x),
      y: Math.min(Math.max(prev.y, 12), maxY > 12 ? maxY : prev.y),
    }));
  }, [visible, isMaximized, isCollapsed]);

  if (!visible) {
    return null;
  }

  const sanitizedTotalTarget = Number.isFinite(totalTarget) ? totalTarget : 0;
  const sanitizedTotalAchieved = Number.isFinite(totalAchieved) ? totalAchieved : 0;
  const sanitizedTotalRemaining = Math.max(totalRemaining, 0);
  const achievementPercentLabel = formatPercent(achievementPercent);
  const achievementPercentTitle = Number.isFinite(achievementPercent)
    ? `${achievementPercent.toFixed(2)}%`
    : '0.00%';

  return (
    <CardContainer ref={cardRef} $x={position.x} $y={position.y} $maximized={isMaximized}>
      <Header onMouseDown={startDrag} $dragging={isDragging}>
        <TitleGroup>
          <Title>
            <DragOutlined style={{ fontSize: 16, color: '#3949ab' }} />
            <span>{title}</span>
          </Title>
          <Subtitle>{highlightedLabel}</Subtitle>
        </TitleGroup>
        <HeaderActions>
          <IconButton type="button" onClick={() => setIsCollapsed((value) => !value)} aria-label="Toggle collapse">
            {isCollapsed ? <PlusOutlined /> : <MinusOutlined />}
          </IconButton>
          <IconButton type="button" onClick={() => setIsMaximized((value) => !value)} aria-label="Toggle maximize">
            {isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
          </IconButton>
          <IconButton type="button" onClick={onClose} aria-label="Close card">
            <CloseOutlined />
          </IconButton>
        </HeaderActions>
      </Header>

      <Content $collapsed={isCollapsed}>
        <SummaryGrid>
          <SummaryCard $variant="primary">
            <SummaryLabel>Total Target</SummaryLabel>
            <Tooltip title={formatExactCurrency(sanitizedTotalTarget)} placement="top">
              <SummaryValue>{formatCurrency(sanitizedTotalTarget)}</SummaryValue>
            </Tooltip>
          </SummaryCard>
          <SummaryCard $variant="success">
            <SummaryLabel>Total Achieved</SummaryLabel>
            <Tooltip title={formatExactCurrency(sanitizedTotalAchieved)} placement="top">
              <SummaryValue>{formatCurrency(sanitizedTotalAchieved)}</SummaryValue>
            </Tooltip>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Target Remaining</SummaryLabel>
            <Tooltip title={formatExactCurrency(sanitizedTotalRemaining)} placement="top">
              <SummaryValue>{formatCurrency(sanitizedTotalRemaining)}</SummaryValue>
            </Tooltip>
          </SummaryCard>
          <SummaryCard $variant="warning">
            <SummaryLabel>Achievement</SummaryLabel>
            <Tooltip title={achievementPercentTitle} placement="top">
              <SummaryValue>{achievementPercentLabel}</SummaryValue>
            </Tooltip>
          </SummaryCard>
        </SummaryGrid>

        <Tooltip title={`${achievementPercentTitle} achieved overall`}>
          <AchievementPill>{achievementPercentLabel} achieved overall</AchievementPill>
        </Tooltip>

        <FooterActions>
          <BreakdownSummary>
            {breakdown.length > 0
              ? `${breakdown.length} state${breakdown.length === 1 ? '' : 's'} in current view`
              : 'No state-level data available for the selected filters.'}
          </BreakdownSummary>
          <ViewMoreButton
            type="button"
            onClick={onViewBreakdown}
            disabled={!breakdown.length}
          >
            View State Breakdown
          </ViewMoreButton>
        </FooterActions>

        {breakdown.length === 0 && <EmptyState>No state-level target data available for the current filters.</EmptyState>}
      </Content>
    </CardContainer>
  );
};

export default RevenueTargetMetricsCard;
